import { FCC, isSuccess, useToasts } from "@dgs/core";
import { Dispatch, SetStateAction, createContext, useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { DataFieldIndexResource } from "../../../shared/types/dataField";
import { contactUpdateInquiryService, useContactUpdateForContact } from "../shared/api/contactUpdateInquiryService";
import { AddressUpdateResource } from "../shared/types/address";
import { ConsentFormValues, ConsentIndexResource } from "../shared/types/consent";
import { ContactCompanyUpdateResource } from "../shared/types/contactCompany";
import { ContactEmailUpdateResource } from "../shared/types/contactEmail";
import { ContactPhoneUpdateResource } from "../shared/types/contactPhone";
import { ContactUpdateInquiryBaseDataResource } from "../shared/types/contactUpdateInquiry";
import { DataFieldValueResource } from "../shared/types/dataField";

interface ContactUpdateInquiryState {
	dataFields: DataFieldIndexResource[];
	contactBaseData: ContactUpdateInquiryBaseDataResource | null;
	addresses: AddressUpdateResource[];
	phoneNumbers: ContactPhoneUpdateResource[];
	emails: ContactEmailUpdateResource[];
	companies: ContactCompanyUpdateResource[];
	dataFieldValues: DataFieldValueResource[];
	setContactBaseData: Dispatch<SetStateAction<ContactUpdateInquiryBaseDataResource | null>>;
	setAddresses: Dispatch<SetStateAction<AddressUpdateResource[]>>;
	setPhoneNumbers: Dispatch<SetStateAction<ContactPhoneUpdateResource[]>>;
	setEmails: Dispatch<SetStateAction<ContactEmailUpdateResource[]>>;
	setCompanies: Dispatch<SetStateAction<ContactCompanyUpdateResource[]>>;
	setDataFieldValues: Dispatch<SetStateAction<DataFieldValueResource[]>>;
	consents: ConsentIndexResource[];
	isLoading: boolean;
	hasUser: boolean;
	createContactUpdateForContact: (values: ConsentFormValues) => Promise<void>;
}

const ContactUpdateInquiryContext = createContext<ContactUpdateInquiryState | undefined>(undefined);

export const ContactUpdateInquiryProvider: FCC = ({ children }) => {
	const { t } = useTranslation();
	const { showToast } = useToasts();
	const navigate = useNavigate();
	const { contactId = "" } = useParams<"contactId">();
	const { id = "" } = useParams<"id">();
	const [contactBaseData, setContactBaseData] = useState<ContactUpdateInquiryBaseDataResource | null>(null);
	const [addresses, setAddresses] = useState<AddressUpdateResource[]>([]);
	const [phoneNumbers, setPhoneNumbers] = useState<ContactPhoneUpdateResource[]>([]);
	const [emails, setEmails] = useState<ContactEmailUpdateResource[]>([]);
	const [companies, setCompanies] = useState<ContactCompanyUpdateResource[]>([]);
	const [dataFieldValues, setDataFieldValues] = useState<DataFieldValueResource[]>([]);

	const { contactUpdate, isLoading } = useContactUpdateForContact(id, contactId);

	const createContactUpdateForContact = useCallback(
		async (values: ConsentFormValues) => {
			const res = await contactUpdateInquiryService.createContactUpdateForContact(id, contactId, {
				id: contactId,
				firstName: contactBaseData?.firstName || "",
				lastName: contactBaseData?.lastName || "",
				addresses: addresses.map((address) => ({
					street: address.street,
					additionalInfo: address.street,
					city: address.city,
					zip: address.zip,
					houseNumber: address.houseNumber,
					type: address.type,
					isPrimary: address.isPrimary,
					countryId: address.country.id,
				})),
				contactPhones: phoneNumbers.map((phone) => ({
					phoneNumber: phone.phoneNumber,
					type: phone.type,
					isPrimary: phone.isPrimary,
				})),
				contactEmails: emails.map((email) => ({
					email: email.email,
					type: email.type,
					isPrimary: email.isPrimary,
				})),
				companies: companies.map((company) => ({
					id: company.id,
					name: company.name,
					position: company.position,
					isPrimary: company.isPrimary,
					primaryAddress: company.primaryAddress,
				})),
				dataFieldValues: dataFieldValues,
				consents: Object.keys(values).map((consentId) => ({ consentId, value: values[consentId] })),
			});

			if (isSuccess(res)) {
				showToast({
					type: "success",
					title: t("Contact update"),
					body: t("Your contact update has been submitted."),
				});
				navigate("../update-success");
			}
		},
		[
			addresses,
			companies,
			contactBaseData?.firstName,
			contactBaseData?.lastName,
			contactId,
			dataFieldValues,
			emails,
			id,
			navigate,
			phoneNumbers,
			showToast,
			t,
		],
	);

	useEffect(() => {
		if (contactUpdate && !isLoading) {
			if (contactUpdate.status === "PLANNED") {
				navigate("../not-yet-open");
			} else if (contactUpdate.status === "COMPLETED") {
				navigate("../closed");
			}
			setContactBaseData({ firstName: contactUpdate.contact.firstName, lastName: contactUpdate.contact.lastName });
			setAddresses(contactUpdate.contact.addresses);
			setPhoneNumbers(contactUpdate.contact.contactPhones);
			setEmails(contactUpdate.contact.contactEmails);
			setCompanies(contactUpdate.contact.companies);
			setDataFieldValues(contactUpdate.contact.dataFieldValues);
		}
	}, [contactUpdate, isLoading, navigate]);

	return (
		<ContactUpdateInquiryContext.Provider
			value={{
				dataFields: contactUpdate?.additionalDataFields || [],
				contactBaseData,
				addresses,
				phoneNumbers,
				emails,
				companies,
				dataFieldValues,
				setContactBaseData,
				setAddresses,
				setPhoneNumbers,
				setEmails,
				setCompanies,
				setDataFieldValues,
				consents: contactUpdate?.consents || [],
				isLoading,
				hasUser: contactUpdate?.contact.hasUser || false,
				createContactUpdateForContact,
			}}
		>
			{children}
		</ContactUpdateInquiryContext.Provider>
	);
};

export const useContactUpdateInquiry = () => {
	const ctx = useContext(ContactUpdateInquiryContext);

	if (ctx === undefined) {
		throw new Error("useContactUpdateInquiry should be wrapped in a ContactUpdateInquiryProvider");
	}

	return ctx;
};
